import { useEffect, useState } from "react";
import { Save, Loader2, Landmark, MapPin } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";

export default function SettingsTab() {
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => setSettings({ bank_details: "", business_address: "" }));
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/settings", settings);
      toast.success("Settings saved", { description: "Bank details and address now appear on every invoice and quote PDF." });
    } catch (e) {
      toast.error("Could not save settings", { description: formatApiError(e) });
    } finally {
      setBusy(false);
    }
  };

  if (!settings) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-brand-crimson" data-testid="settings-loading-spinner" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-6" data-testid="settings-tab">
      <section className="rounded-lg border border-brand-line bg-brand-surface p-6" data-testid="settings-bank-section">
        <p className="flex items-center gap-2 font-eyebrow text-[10px] uppercase tracking-[0.25em] text-brand-crimson font-bold mb-4">
          <Landmark className="h-4 w-4" /> Bank Details (shown on invoices & quotes)
        </p>
        <Textarea
          data-testid="settings-bank-details"
          value={settings.bank_details || ""}
          onChange={(e) => setSettings((s) => ({ ...s, bank_details: e.target.value }))}
          rows={4}
          placeholder={"Account name: Quest K9's Ltd\nSort code: 00-00-00\nAccount no: 00000000"}
          className="bg-brand-obsidian border-brand-line"
        />
      </section>

      <section className="rounded-lg border border-brand-line bg-brand-surface p-6" data-testid="settings-address-section">
        <p className="flex items-center gap-2 font-eyebrow text-[10px] uppercase tracking-[0.25em] text-brand-crimson font-bold mb-4">
          <MapPin className="h-4 w-4" /> Your Business Address (shown on invoices & quotes)
        </p>
        <Textarea
          data-testid="settings-business-address"
          value={settings.business_address || ""}
          onChange={(e) => setSettings((s) => ({ ...s, business_address: e.target.value }))}
          rows={4}
          placeholder={"Quest K9's Detection Dogs\nYour street address\nTown, County, Postcode"}
          className="bg-brand-obsidian border-brand-line"
        />
      </section>

      <Button onClick={save} disabled={busy} data-testid="settings-save-button" className="w-full bg-brand-crimson hover:bg-brand-crimhover text-white font-eyebrow uppercase tracking-[0.2em] text-xs font-bold py-6">
        {busy ? "Saving..." : "Save Settings"} <Save className="h-4 w-4" />
      </Button>
    </div>
  );
}
